// v6 F16 S4 — Frontend auto_source resolver.
//
// Spiegelt backend `resolve_auto_source()` in server.py zodat de live UI-preview
// (offerte-totalen in de meetpagina) dezelfde qty berekent als de backend bij
// het opslaan / PDF genereren. Ondersteunt:
//
//   scope "stair"   : stair.tread_count, stair.landing_m2
//   scope "zone"    : zone.net_area, zone.perimeter
//   scope "joinery" : joinery.<part_key>, joinery.pieces[:kind_filter]
//                    en alias-bronnen met kind_filter (bv. door_blade_ext)
//
// De `autoSources` catalog wordt via GET /api/auto-sources geladen en levert
// eventuele `part_key` + `kind_filter` overrides voor alias-bronnen.
//
// PART_FORMULA en mergePartStates worden geïmporteerd uit JoineryBlock zodat
// we exact dezelfde formules gebruiken als de object-editor.

import React from "react";
import { PART_FORMULA, mergePartStates } from "../components/measure/JoineryBlock";
import { computeZoneArea } from "./calculator";

// v6 F42k P2 v12 — Vereenvoudigd schrijnwerk-model. Spiegelt exact
// `compute_joinery_object_quantities()` (server.py) wanneer obj.simple_mode=true,
// gesplitst per kind-familie (raam / deur / overig).
// v14f-fix: `measurement_mode` heeft voorrang; levert PURE m² of PURE lm output.
function simpleModeQuantities(obj) {
  const L = +obj.length || 0;
  const H = +obj.height || 0;
  const objCount = Math.max(1, +obj.count || 1);
  const kind = obj.kind || "";
  const isWin = kind.includes("window");
  const isDoor = kind.includes("door") || kind.includes("gate");
  const sidesMult = (obj.window_sides || "outside") === "both" ? 2 : 1;
  const byKey = {};

  // v14f-fix: expliciete measurement_mode overrides.
  const _mm = obj.measurement_mode;
  if (_mm === "area_m2") {
    let ratio = +obj.simple_wood_ratio;
    if (!Number.isFinite(ratio)) ratio = 1.0;
    ratio = Math.min(1, Math.max(0, ratio));
    const effRatio = isDoor ? ratio : 1.0;
    let area = L * H * objCount * sidesMult * effRatio;
    const sl = obj?.sidelights;
    if (isDoor && sl?.enabled) {
      const slQty = (+sl.count || 0) * (+sl.width || 0) * (+sl.height || 0) * objCount * sidesMult;
      if (slQty > 0) area += slQty;
    }
    if (area > 0) {
      const key = isWin ? "window_pane" : (isDoor ? "door_blade" : "front");
      byKey[key] = area;
    }
    return { area_m2: area, length_lm: 0, byKey };
  }
  if (_mm === "frame_lm") {
    if (isWin) {
      const fixed = Math.max(0, +obj.simple_fixed_parts || 0);
      const moving = Math.max(0, +obj.simple_moving_parts || 0);
      const forceSliding = kind === "exterior_sliding_window";
      let nFixed = fixed, nMoving = moving;
      let movingWidths = Array.isArray(obj.window_moving_widths)
        ? obj.window_moving_widths.map((w) => +w || 0).filter((w) => w > 0)
        : [];
      if (obj.window_sliding || forceSliding) {
        let split = +obj.window_sliding_split_ratio;
        if (!Number.isFinite(split)) split = 0.5;
        split = Math.min(1, Math.max(0, split));
        nFixed = 1; nMoving = 1;
        movingWidths = [L * (1 - split)];
      } else {
        const nt = Math.max(1, nFixed + nMoving);
        const dw = L / nt;
        while (movingWidths.length < nMoving) movingWidths.push(dw);
        movingWidths = movingWidths.slice(0, nMoving);
      }
      const nTotal = nFixed + nMoving;
      let nInterne = Math.max(0, nTotal - 1);
      const effNoMiddle = !!obj.window_double_movers_no_middle || (obj.window_middle_pillar === false);
      if (effNoMiddle && !(obj.window_sliding || forceSliding)) nInterne = Math.max(0, nInterne - 1);
      const nVerticalen = 2 + nInterne;
      const hOuter = 2 * L;
      const vFixed = nVerticalen * H;
      const hMoving = movingWidths.reduce((s, w) => s + 2 * w, 0);
      const vMoving = nMoving * 2 * H;
      const toplightLm = obj.window_has_toplight ? L : 0;
      const lmPer = hOuter + vFixed + hMoving + vMoving + toplightLm;
      const totalLm = lmPer * objCount * sidesMult;
      if (totalLm > 0) byKey.frame = totalLm;
      return { area_m2: 0, length_lm: totalLm, byKey };
    }
    if (isDoor) {
      const kaderPer = 2 * H + L + (obj.door_double ? H : 0);
      const totalLm = kaderPer * objCount * sidesMult;
      if (totalLm > 0) byKey.frame = totalLm;
      return { area_m2: 0, length_lm: totalLm, byKey };
    }
    const totalLm = 2 * (L + H) * objCount * sidesMult;
    if (totalLm > 0) byKey.frame = totalLm;
    return { area_m2: 0, length_lm: totalLm, byKey };
  }

  // Legacy pad (measurement_mode == null): v13-gedrag
  const fixed = Math.max(0, +obj.simple_fixed_parts || 0);
  const moving = Math.max(0, +obj.simple_moving_parts || 0);
  let ratio = +obj.simple_wood_ratio;
  if (!Number.isFinite(ratio)) ratio = 0.30;
  ratio = Math.min(1, Math.max(0, ratio));
  const forceSliding = kind === "exterior_sliding_window";

  if (isWin) {
    if (obj.window_has_cross_division) {
      const area = L * H * objCount * sidesMult;
      if (area > 0) byKey.window_pane = area;
      return { area_m2: area, length_lm: 0, byKey };
    }
    let nFixed = fixed, nMoving = moving;
    let movingWidths = Array.isArray(obj.window_moving_widths)
      ? obj.window_moving_widths.map((w) => +w || 0).filter((w) => w > 0)
      : [];
    if (obj.window_sliding || forceSliding) {
      let split = +obj.window_sliding_split_ratio;
      if (!Number.isFinite(split)) split = 0.5;
      split = Math.min(1, Math.max(0, split));
      nFixed = 1; nMoving = 1;
      movingWidths = [L * (1 - split)];
    } else {
      const nTotalW = Math.max(1, nFixed + nMoving);
      const defaultW = L / nTotalW;
      while (movingWidths.length < nMoving) movingWidths.push(defaultW);
      movingWidths = movingWidths.slice(0, nMoving);
    }
    const nTotal = nFixed + nMoving;
    let nInterne = Math.max(0, nTotal - 1);
    const effectiveNoMiddle = !!obj.window_double_movers_no_middle || (obj.window_middle_pillar === false);
    if (effectiveNoMiddle && !(obj.window_sliding || forceSliding)) nInterne = Math.max(0, nInterne - 1);
    const nVerticalen = 2 + nInterne;
    const hOuter = 2 * L;
    const vFixed = nVerticalen * H;
    const hMoving = movingWidths.reduce((s, w) => s + 2 * w, 0);
    const vMoving = nMoving * 2 * H;
    const toplightLm = obj.window_has_toplight ? L : 0;
    const lmPer = hOuter + vFixed + hMoving + vMoving + toplightLm;
    const totalLm = lmPer * objCount * sidesMult;
    if (totalLm > 0) byKey.frame = totalLm;
    return { area_m2: 0, length_lm: totalLm, byKey };
  }

  if (isDoor) {
    const doorArea = L * H * ratio * objCount * sidesMult;
    let kaderPer = 2 * H + L;
    if (obj.door_double) kaderPer += H;
    const kaderLm = kaderPer * objCount * sidesMult;
    let area = doorArea;
    const sl = obj?.sidelights;
    if (sl?.enabled) {
      const slQty = (+sl.count || 0) * (+sl.width || 0) * (+sl.height || 0) * objCount * sidesMult;
      if (slQty > 0) area += slQty;
    }
    if (area > 0) byKey.door_blade = doorArea;
    if (kaderLm > 0) byKey.frame = kaderLm;
    return { area_m2: area, length_lm: kaderLm, byKey };
  }

  const totalParts = Math.max(1, fixed + moving);
  const totalFace = L * H * totalParts * objCount;
  const woodArea = totalFace * ratio;
  const frameLength = 2 * (L + H) * objCount;
  if (woodArea > 0) byKey.front = woodArea;
  if (frameLength > 0) byKey.frame = frameLength;
  return { area_m2: woodArea, length_lm: frameLength, byKey };
}

// v6 F16 S4 — React Context zodat kleine componenten (MeasurementRow,
// action-rows in RoomQuickFlow, PDF-preview, ...) de auto-source catalog
// kunnen ophalen zonder deep prop-drilling.
export const AutoSourcesContext = React.createContext({});

/**
 * Los een auto_source key op naar een numerieke qty binnen de gegeven zone.
 * Retourneert 0 bij onbekende bron, ontbrekende data, of foutieve input.
 * Nooit een exception naar de calc-laag.
 *
 * @param {string|null} sourceKey  — bv. "joinery.door_blade", "stair.tread_count"
 * @param {object}      zone       — het WorkZone object uit quote.work_zones
 * @param {object}      autoSources — catalog uit GET /api/auto-sources (voor kind_filter)
 * @param {object}      partsDefaults — kind → parts[] uit GET /api/joinery/parts-defaults
 * @returns {number}
 */
export function resolveAutoSource(sourceKey, zone, autoSources = {}, partsDefaults = {}) {
  if (!sourceKey || !zone) return 0;

  // Parse scope + optional inline kind-filter (":kind")
  const dot = sourceKey.indexOf(".");
  if (dot < 0) return 0;
  const scope = sourceKey.slice(0, dot);
  const rest = sourceKey.slice(dot + 1);
  const colon = rest.indexOf(":");
  const key = colon >= 0 ? rest.slice(0, colon) : rest;
  const inlineKindFilter = colon >= 0 ? rest.slice(colon + 1) : null;

  // Catalog-driven overrides (part_key + kind_filter) voor alias-bronnen
  // zoals joinery.door_blade_ext → part_key=door_blade, kind_filter=[exterior_door]
  const meta = autoSources[sourceKey] || {};
  const partKey = meta.part_key || key;
  let kindFilter = meta.kind_filter || (inlineKindFilter ? [inlineKindFilter] : null);

  try {
    // ── STAIR ────────────────────────────────────────────────────────
    if (scope === "stair") {
      if (key === "tread_count") return Math.max(0, +zone.dim_length || 0);
      if (key === "landing_m2") return Math.max(0, +zone.landing_m2 || 0);
      return 0;
    }

    // ── ZONE ─────────────────────────────────────────────────────────
    if (scope === "zone") {
      if (key === "net_area") {
        const { net } = computeZoneArea(zone);
        return Math.max(0, net);
      }
      if (key === "perimeter") {
        const L = +zone.dim_length || 0;
        const H = +zone.dim_height || 0;
        return Math.max(0, 2 * (L + H));
      }
      return 0;
    }

    // ── JOINERY ──────────────────────────────────────────────────────
    if (scope === "joinery") {
      const objects = zone.joinery_objects || [];
      if (objects.length === 0) return 0;

      // "pieces" — telt objecten (met optionele kind-filter)
      if (partKey === "pieces") {
        let total = 0;
        for (const obj of objects) {
          if (kindFilter && !kindFilter.includes(obj.kind)) continue;
          total += Math.max(1, +obj.count || 1);
        }
        return total;
      }

      // v6 F20 — Totaal actieve m² binnen de zone: som over ALLE
      // joinery-objects van alle m²-parts (enabled=True). Zowel
      // `total_m2` (binnen) als `total_m2_ext` (buiten) gebruiken dezelfde
      // logica; scope-filtering gebeurt via de zone zelf.
      if (partKey === "total_m2" || partKey === "total_m2_ext") {
        let total = 0;
        for (const obj of objects) {
          if ((obj.measure_mode || "per_piece") === "forfait") continue;
          if (obj.simple_mode) {
            total += simpleModeQuantities(obj).area_m2;
            continue;
          }
          const kindDefaults = partsDefaults[obj.kind] || [];
          const states = mergePartStates(obj, kindDefaults);
          const formulas = PART_FORMULA[obj.kind] || {};
          const objCount = Math.max(1, +obj.count || 1);
          for (const p of states) {
            if (!p.enabled || p.count <= 0) continue;
            if (p.unit !== "m2") continue;
            const fn = formulas[p.key];
            if (!fn) continue;
            const q = fn(obj) * p.count * (p.sides || 1) * objCount;
            if (q > 0) total += q;
          }
        }
        return Math.max(0, total);
      }

      // v6 F31 — Totaal actieve lm van kaders/lijsten (buiten-context).
      // Zelfde logica als total_m2_ext, maar filtert op unit="lm" ipv "m2".
      if (partKey === "total_lm_ext") {
        let total = 0;
        for (const obj of objects) {
          if ((obj.measure_mode || "per_piece") === "forfait") continue;
          if (obj.simple_mode) {
            total += simpleModeQuantities(obj).length_lm;
            continue;
          }
          const kindDefaults = partsDefaults[obj.kind] || [];
          const states = mergePartStates(obj, kindDefaults);
          const formulas = PART_FORMULA[obj.kind] || {};
          const objCount = Math.max(1, +obj.count || 1);
          for (const p of states) {
            if (!p.enabled || p.count <= 0) continue;
            if (p.unit !== "lm") continue;
            const fn = formulas[p.key];
            if (!fn) continue;
            const q = fn(obj) * p.count * (p.sides || 1) * objCount;
            if (q > 0) total += q;
          }
        }
        return Math.max(0, total);
      }

      // Part-key sum via PART_FORMULA
      // v6 F16 S5 — Ondersteun multi-part_keys (meta.part_keys=[...])
      const partKeysList = meta.part_keys || null;
      const targetKeys = partKeysList ? new Set(partKeysList) : new Set([partKey]);
      let total = 0;
      for (const obj of objects) {
        if (kindFilter && !kindFilter.includes(obj.kind)) continue;
        // Skip forfait modus (geen parts-berekening)
        if ((obj.measure_mode || "per_piece") === "forfait") continue;

        // v6 F42k P2 — Simple mode: gebruik de compacte byKey lookup.
        if (obj.simple_mode) {
          const q = simpleModeQuantities(obj);
          for (const k of targetKeys) {
            if (q.byKey[k]) total += q.byKey[k];
          }
          continue;
        }

        const kindDefaults = partsDefaults[obj.kind] || [];
        const states = mergePartStates(obj, kindDefaults);
        const formulas = PART_FORMULA[obj.kind] || {};
        const objCount = Math.max(1, +obj.count || 1);

        for (const p of states) {
          if (!targetKeys.has(p.key)) continue;
          if (!p.enabled || p.count <= 0) continue;
          const fn = formulas[p.key];
          if (!fn) continue;
          const q = fn(obj) * p.count * (p.sides || 1) * objCount;
          if (q > 0) total += q;
        }
      }
      return Math.max(0, total);
    }
  } catch {
    return 0;
  }
  return 0;
}

/**
 * Label-lookup uit de auto-sources catalog.
 * @returns {string|null}
 */
export function autoSourceLabel(sourceKey, autoSources) {
  if (!sourceKey || !autoSources) return null;
  return autoSources[sourceKey]?.label || null;
}

/**
 * v6 F42k v14d — Spiegel van backend `compute_joinery_object_quantities` per object.
 * Retourneert `{area_m2, length_lm, pieces}` voor één joinery-object.
 * Gebruikt in calculator.js path (A) fallback voor custom joinery_works
 * met auto_source=null en pc.unit ∈ (m2/lm/piece) — spiegelt server.py r4364-4383.
 *
 * @param {object} obj             — één joinery_object
 * @param {object} partsDefaults   — kind → parts[] uit GET /api/joinery/parts-defaults
 * @returns {{area_m2: number, length_lm: number, pieces: number}}
 */
export function computeJoineryObjectQty(obj, partsDefaults = {}) {
  if (!obj) return { area_m2: 0, length_lm: 0, pieces: 0 };
  const objCount = Math.max(1, +obj.count || 1);
  const mode = obj.measure_mode || "per_piece";

  // Forfait: negeer parts; pieces = count als forfait_amount > 0
  if (mode === "forfait") {
    const fa = +obj.forfait_amount || 0;
    return { area_m2: 0, length_lm: 0, pieces: fa > 0 ? objCount : 0 };
  }

  // Simple mode: gebruik de compacte simpleModeQuantities helper
  if (obj.simple_mode) {
    const q = simpleModeQuantities(obj);
    return {
      area_m2: q.area_m2 || 0,
      length_lm: q.length_lm || 0,
      pieces: objCount,
    };
  }

  // Parts-based: aggregate over enabled parts met formula
  let areaM2 = 0;
  let lengthLm = 0;
  try {
    const kindDefaults = partsDefaults[obj.kind] || [];
    const states = mergePartStates(obj, kindDefaults);
    const formulas = PART_FORMULA[obj.kind] || {};
    for (const p of states) {
      if (!p.enabled || (p.count || 0) <= 0) continue;
      const fn = formulas[p.key];
      if (!fn) continue;
      const q = fn(obj) * (p.count || 0) * (p.sides || 1) * objCount;
      if (q <= 0) continue;
      if (p.unit === "m2") areaM2 += q;
      else if (p.unit === "lm") lengthLm += q;
    }
  } catch {
    // fall through
  }
  return { area_m2: areaM2, length_lm: lengthLm, pieces: objCount };
}
